import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Shield, Mail, Phone, Key, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminRecovery = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: method, 3: code
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState('email'); // 'email', 'sms', 'backup'
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    if (method === 'backup') {
      setStep(3);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/admin-security/request-recovery`, {
        email,
        method,
      });
      toast.success(`Verification code sent to your recovery ${method === 'email' ? 'email' : 'phone'}`);
      setStep(3);
    } catch (error) {
      console.error('Request recovery error:', error);
      toast.error(error.response?.data?.error || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (method === 'backup') {
      if (!backupCode) {
        toast.error('Please enter your backup code');
        return;
      }

      setLoading(true);

      try {
        const response = await axios.post(`${API_URL}/admin-security/verify-backup-code`, {
          email,
          backupCode,
        });
        toast.success('Backup code verified! Redirecting to password reset...');
        
        // Redirect to reset password page with token
        setTimeout(() => {
          navigate(`/reset-password?token=${response.data.token}&request=${response.data.userId}`);
        }, 1000);
      } catch (error) {
        console.error('Verify backup code error:', error);
        toast.error(error.response?.data?.error || 'Invalid backup code');
      } finally {
        setLoading(false);
      }
    } else {
      if (!code) {
        toast.error('Please enter the verification code');
        return;
      }

      setLoading(true);

      try {
        const response = await axios.post(`${API_URL}/admin-security/verify-recovery`, {
          email,
          code,
          method,
        });
        toast.success('Code verified! Redirecting to password reset...');
        
        // Redirect to reset password page with token
        setTimeout(() => {
          navigate(`/reset-password?token=${response.data.token}&request=${response.data.userId}`);
        }, 1000);
      } catch (error) {
        console.error('Verify code error:', error);
        toast.error(error.response?.data?.error || 'Invalid or expired code');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="text-primary-600" size={32} />
          </div>
          <h2 className="text-3xl font-bold mb-2">Admin Account Recovery</h2>
          <p className="text-gray-600">
            {step === 1 && 'Enter your admin email to begin recovery'}
            {step === 2 && 'Choose your recovery method'}
            {step === 3 && method === 'backup' && 'Enter your backup code'}
            {step === 3 && method !== 'backup' && 'Enter the verification code'}
          </p>
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={() => setStep(2)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="admin@example.com"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full"
            >
              Continue
            </button>
          </form>
        )}

        {/* Step 2: Method Selection */}
        {step === 2 && (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Choose Recovery Method
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 transition-colors">
                  <input
                    type="radio"
                    value="email"
                    checked={method === 'email'}
                    onChange={(e) => setMethod(e.target.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <Mail className="ml-3 mr-2 text-gray-400" size={20} />
                  <div className="flex-1">
                    <div className="font-medium">Recovery Email</div>
                    <div className="text-sm text-gray-500">Send code to your recovery email</div>
                  </div>
                </label>

                <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 transition-colors">
                  <input
                    type="radio"
                    value="sms"
                    checked={method === 'sms'}
                    onChange={(e) => setMethod(e.target.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <Phone className="ml-3 mr-2 text-gray-400" size={20} />
                  <div className="flex-1">
                    <div className="font-medium">Recovery Phone</div>
                    <div className="text-sm text-gray-500">Send code via SMS</div>
                  </div>
                </label>

                <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary-300 transition-colors">
                  <input
                    type="radio"
                    value="backup"
                    checked={method === 'backup'}
                    onChange={(e) => setMethod(e.target.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <Key className="ml-3 mr-2 text-gray-400" size={20} />
                  <div className="flex-1">
                    <div className="font-medium">Backup Code</div>
                    <div className="text-sm text-gray-500">Use a one-time backup code</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn btn-secondary flex-1"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary flex-1"
              >
                {loading ? 'Sending...' : method === 'backup' ? 'Continue' : 'Send Code'}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Code Verification */}
        {step === 3 && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            {method === 'backup' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Backup Code
                </label>
                <input
                  type="text"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  className="input text-center font-mono text-lg tracking-wider"
                  placeholder="XXXXXXXX"
                  required
                  disabled={loading}
                  maxLength={8}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter one of your 8-character backup codes
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="input text-center font-mono text-2xl tracking-widest"
                  placeholder="000000"
                  required
                  disabled={loading}
                  maxLength={6}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Check your {method === 'email' ? 'recovery email' : 'phone'} for the 6-digit code
                </p>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="btn btn-secondary flex-1"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary flex-1"
              >
                {loading ? 'Verifying...' : 'Verify & Reset Password'}
              </button>
            </div>

            {method !== 'backup' && (
              <button
                type="button"
                onClick={handleRequestCode}
                disabled={loading}
                className="text-sm text-primary-600 hover:text-primary-700 w-full"
              >
                Resend Code
              </button>
            )}
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center space-x-1">
            <ArrowLeft size={16} />
            <span>Back to Login</span>
          </Link>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Admin recovery requires that you've previously set up recovery contacts in your security settings. If you haven't configured recovery options, please contact system support.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminRecovery;
